/**
 * RateCompareChart.jsx — "Rate vs market" view for one commodity.
 *
 * Plots your billed SUPPLY rate ($/usage-unit) against the wholesale reference
 * hub rate, so you can see whether your effective commodity rate is above or
 * below market (in/out-of-the-money). Delivery, tax & fees are excluded because
 * they are not market products.
 */
import React from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { TrendingDown, TrendingUp } from 'lucide-react';
import { Badge } from '../ui/badge';

const fmtRate = (v, uom) => (v == null ? '—' : `$${Number(v).toFixed(4)}/${uom}`);

export function RateCompareChart({ commodity, label, months, billed, market, uom,
  avgBilled, avgMarket, position, feedLabel, height = 260 }) {
  const data = months.map((m, i) => ({
    month: m.slice(2),
    billed: billed?.[i] ?? null,
    market: market?.[i] ?? null,
  }));
  const below = position === 'below';
  const above = position === 'above';
  const deltaPct = (avgBilled != null && avgMarket) ? ((avgBilled - avgMarket) / avgMarket) * 100 : null;

  return (
    <div className="border rounded-lg bg-card p-3" data-testid={`rate-compare-${commodity}`}>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <h3 className="text-sm font-semibold truncate">{label} — rate vs market</h3>
          {feedLabel && <Badge variant="outline" className="text-[10px]">{feedLabel}</Badge>}
        </div>
        {position && (
          <Badge
            className={`border-0 gap-1 ${below ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}
            data-testid={`rate-position-${commodity}`}
          >
            {below ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
            {below ? 'Below market' : 'Above market'}
            {deltaPct != null && ` (${deltaPct > 0 ? '+' : ''}${deltaPct.toFixed(0)}%)`}
          </Badge>
        )}
      </div>
      <div className="flex items-center gap-4 text-[11px] text-muted-foreground mb-1.5">
        <span>Your supply avg: <span className="font-mono text-foreground">{fmtRate(avgBilled, uom)}</span></span>
        <span>Market avg: <span className="font-mono text-foreground">{fmtRate(avgMarket, uom)}</span></span>
      </div>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 8, right: 8, left: 4, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
          <XAxis dataKey="month" tick={{ fontSize: 10 }} />
          <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${Number(v).toFixed(3)}`} width={56} />
          <Tooltip formatter={(v) => (v == null ? '—' : `$${Number(v).toFixed(4)}/${uom}`)} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Line type="monotone" dataKey="market" stroke="#10b981" strokeWidth={2.5}
            strokeDasharray="5 4" dot={false} name="Wholesale market" connectNulls />
          <Line type="monotone" dataKey="billed" stroke="#0ea5e9" strokeWidth={2.5}
            dot={false} name="Your billed supply" connectNulls />
        </LineChart>
      </ResponsiveContainer>
      <p className="text-[11px] text-muted-foreground mt-1.5">
        Wholesale reference hub vs your billed <span className="font-medium">supply</span> rate
        (delivery, tax &amp; fees excluded — not market products). Your retail rate can sit above
        wholesale due to margin/capacity; large gaps may also indicate bill-classification noise.
      </p>
    </div>
  );
}
